import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { collection, getDocs } from "firebase/firestore";
import {
  MAIN_THEME_COLOR,
  SECONDARY_THEME_COLOR,
} from "../../providers/theme/colors/colors";
import { firestore } from "../../index";
import {
  CURRENT_BOOK_BASE_YEAR,
  EditorialCommitteeMember,
  getCurrentBookNumber,
  historicalEditors,
} from "./yuvagaaz_data";

type EditorEntry = {
  id: string;
  year: string;
  name: string;
  place?: string;
  img?: string;
  highlight?: boolean;
};

export default function EditorsTimeline() {
  const [committeeMembers, setCommitteeMembers] = useState<EditorialCommitteeMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMembers() {
      try {
        const snapshot = await getDocs(collection(firestore, "yuvagaaz_editorial_committee"));
        setCommitteeMembers(
          snapshot.docs.map((memberDoc) => ({
            docId: memberDoc.id,
            ...(memberDoc.data() as Omit<EditorialCommitteeMember, "docId">),
          })),
        );
      } catch (error) {
        console.error("Failed to load editor timeline data", error);
      } finally {
        setLoading(false);
      }
    }

    fetchMembers();
  }, []);

  const editors = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const currentBookNumber = getCurrentBookNumber(currentYear);
    const currentEditor = [...committeeMembers]
      .filter((member) => member.post.trim().toLowerCase() === "editor")
      .sort((memberA, memberB) => {
        const timeA = new Date((memberA as any).updatedAt || (memberA as any).createdAt || 0).getTime();
        const timeB = new Date((memberB as any).updatedAt || (memberB as any).createdAt || 0).getTime();
        return timeB - timeA;
      })[0];

    const timelineEntries: EditorEntry[] = [];

    if (currentEditor) {
      timelineEntries.push({
        id: `current-${currentYear}`,
        year: `Book ${currentBookNumber} - ${currentYear}`,
        name: currentEditor.name,
        place: currentEditor.place,
        img: currentEditor.img,
        highlight: true,
      });
    }

    historicalEditors
      .filter(
        (entry) =>
          !(
            currentEditor &&
            currentYear === CURRENT_BOOK_BASE_YEAR &&
            entry.yearValue === CURRENT_BOOK_BASE_YEAR
          ),
      )
      .forEach((entry) => {
        timelineEntries.push({
          id: `history-${entry.id}`,
          year: entry.bookLabel,
          name: entry.name,
          place: entry.place,
          img: entry.img,
          highlight: false,
        });
      });

    return timelineEntries;
  }, [committeeMembers]);

  return (
    <Box my={7} px={{ xs: 1, sm: 0 }} id="editorsTimeline">
      <Box
        mb={4}
        px={{ xs: 2, md: 3 }}
        py={{ xs: 3, md: 4 }}
        sx={{
          borderRadius: { xs: 3, md: 4 },
          color: "#fff",
          background: `linear-gradient(120deg, ${MAIN_THEME_COLOR} 0%, #1f6ea9 50%, ${SECONDARY_THEME_COLOR} 100%)`,
          boxShadow: "0 20px 44px rgba(12, 58, 98, 0.18)",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={800}
          gutterBottom
          sx={{ fontSize: { xs: 24, sm: 30 } }}
        >
          Yuvagaaz Editors Through The Years
        </Typography>
        <Typography
          variant="body1"
          sx={{
            opacity: 0.9,
            maxWidth: 760,
            lineHeight: 1.8,
            fontSize: { xs: 14, sm: 16 },
          }}
        >
          A dedicated space for this year&apos;s editor and the previous
          editors who shaped the voice of Yuvagaaz.
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
      <Grid container spacing={3} justifyContent="center">
        {editors.map((editor) => (
          <Grid item xs={12} sm={6} md={4} lg={4} key={editor.id}>
            <Card
              sx={{
                height: "100%",
                borderRadius: { xs: 3, md: 4 },
                position: "relative",
                overflow: "hidden",
                border: editor.highlight
                  ? `1px solid ${MAIN_THEME_COLOR}`
                  : `1px solid rgba(31, 110, 169, 0.12)`,
                boxShadow: editor.highlight
                  ? "0 18px 36px rgba(25, 92, 142, 0.18)"
                  : "0 12px 26px rgba(0, 0, 0, 0.08)",
                background: editor.highlight
                  ? "linear-gradient(180deg, rgba(232, 246, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)"
                  : "#fff",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  color: editor.highlight ? "#fff" : MAIN_THEME_COLOR,
                  background: editor.highlight
                    ? `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, #2b82c3 100%)`
                    : "rgba(56, 136, 194, 0.08)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  letterSpacing={0.8}
                >
                  {editor.year}
                </Typography>
              </Box>

              <CardContent sx={{ p: 3, textAlign: "center" }}>
                {editor.img ? (
                  <Box
                    component="img"
                    src={editor.img}
                    alt={editor.name}
                    sx={{
                      width: { xs: 150, sm: 170, md: 180 },
                      height: { xs: 180, sm: 200, md: 215 },
                      mx: "auto",
                      mb: 2,
                      borderRadius: 3,
                      objectFit: "cover",
                      objectPosition: "center top",
                      border: `4px solid ${SECONDARY_THEME_COLOR}`,
                      boxShadow: "0 16px 28px rgba(56, 136, 194, 0.16)",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: { xs: 150, sm: 170, md: 180 },
                      height: { xs: 180, sm: 200, md: 215 },
                      mx: "auto",
                      mb: 2,
                      borderRadius: 3,
                      border: `4px solid ${SECONDARY_THEME_COLOR}`,
                      bgcolor: "rgba(56, 136, 194, 0.12)",
                      color: MAIN_THEME_COLOR,
                      fontWeight: 800,
                      fontSize: "2.5rem",
                      boxShadow: "0 16px 28px rgba(56, 136, 194, 0.16)",
                    }}
                  >
                    {editor.name
                      .split(" ")
                      .map((part) => part[0])
                      .slice(0, 2)
                      .join("")}
                  </Avatar>
                )}

                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ mb: 0.75, fontSize: { xs: 18, md: 20 } }}
                >
                  {editor.name}
                </Typography>
                {editor.place ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75, lineHeight: 1.5 }}
                  >
                    {editor.place}
                  </Typography>
                ) : null}
                <Box
                  sx={{
                    mx: "auto",
                    width: "fit-content",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: "rgba(56, 136, 194, 0.08)",
                  }}
                ></Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      )}
    </Box>
  );
}
